import 'intersection-observer';
import * as React from "react";
import { InView } from 'react-intersection-observer';
import { IOWidget, Updater, fromJSX } from "widgets-for-react";
import { blockInviewHandler } from "../../../drupal/rendering/inview_utils.mod";
import { ImageSize, Link } from "../types.gen";
import { getClassName, link } from "../utils.gen";
import { ImageContainer } from "./ImageContainer.gen";
import { CMSState } from '../../../publicSite/drupalPage/state'

export type ButtonProps = (
  | { kind: "button"; on_click: () => Updater<CMSState> }
  | Link ) & { title: string }

export type TextImageBlockProps = {
  index: number
  imageLocation: "left" | "right"
  image: number
  image_size: ImageSize
  image_alt_text: string
  label?: string
  title: React.ReactNode
  subtitle?: React.ReactNode
  text: string
  primary_button?: ButtonProps
  secondary_button?: ButtonProps
  link_type: boolean
  get_default_data_src:(() => ImageSize),
  get_default_data_srcset:(() => [ImageSize, string][])
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type TextImageBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  content_classname: string
  header_classname: string
  label_classname: string
  title_classname: string
  subtitle_classname: string
  text_classname: string
  intro_classname: string
  footer_classname: string
  clickable_wrapper_classname: string
  clickable_primary_classname: string
  clickable_secondary_classname: string
  figure_classname: string
  image_classname: string
}>

export function TextImageBlock(
  props: TextImageBlockProps,
  options?: { style?: TextImageBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return cmsState => {
    return fromJSX(
      onDone => {
        return (
          <InView
            as="section"
            onChange={(inView) => blockInviewHandler('textImage', inView, `io-${options && options.key ? options.key : props.index}`, getClassName(options, "inview_classname", "inview"))}
            triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
            threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
            id={`io-${options && options.key ? options.key : props.index}`}
            className={getClassName(options, "block_classname", "container")}
            key={options && options.key ? options.key : `block-${props.index}`}
          >
            <div className={getClassName(options, "inner_classname", "py-5 row")}>
              <div className={getClassName(options, "content_classname", `col-md-6 mb-4 order-${props.imageLocation == "left" ? 2 : 1}`)}>
                <header className={getClassName(options, "header_classname", "")}>
                  {props.label && (<span className={getClassName(options, "label_classname", "d-block small")}>{props.label}</span>)}
                  <h2 className={getClassName(options, "title_classname", "mb-3")}>{props.title}</h2>
                  {props.subtitle && (<p className={getClassName(options, "subtitle_classname", "mb-3 text-muted")}>{props.subtitle}</p>)}
                </header>

                <div className={getClassName(options, "text_classname", "")}>
                  <div className={getClassName(options, "intro_classname", "")} dangerouslySetInnerHTML={{ __html: props.text }} />
                </div>

                {props.primary_button || props.secondary_button
                  ? <footer className={getClassName(options, "footer_classname", "")}>
                      {props.primary_button && (
                        <div className={getClassName(options, "clickable_wrapper_classname", "d-inline-block")}>
                          {props.primary_button.kind === "button" ? (
                            <button
                              className={props.link_type === false ? getClassName(options, "clickable_primary_classname", "btn btn-dark mr-2") : getClassName(options, "clickable_secondary_classname", "btn btn-link px-0 mr-2")}
                              onClick={() => props.primary_button.kind === "button" && onDone(props.primary_button.on_click())}
                            >
                              {props.primary_button.title}
                            </button>
                          ) : props.primary_button.title && props.primary_button.url
                            ? (link(props.primary_button,
                                    {classname: props.link_type === false ? getClassName(options, "clickable_primary_classname", "btn btn-dark mr-2") : getClassName(options, "clickable_secondary_classname", "btn btn-link px-0 mr-2")},
                                    props.primary_button.title))
                            : null
                          }
                        </div>
                      )}
                      
                      {props.secondary_button && (
                        <div className={getClassName(options, "clickable_wrapper_classname", "d-inline-block")}>
                          {props.secondary_button.kind === "button" ? (
                            <button
                              className={getClassName(options, "clickable_secondary_classname", "btn btn-dark")}
                              onClick={() => props.secondary_button.kind === "button" && onDone(props.secondary_button.on_click())}
                            >
                              {props.secondary_button.title}
                            </button>
                          ) : props.secondary_button.title && props.secondary_button.url
                            ? (link(props.secondary_button,
                                    {classname: getClassName(options, "clickable_secondary_classname", "btn btn-link px-0")},
                                    props.secondary_button.title))
                            : null
                          }
                        </div>
                      )}
                    </footer>
                  : null
                }
              </div>

              <div className={getClassName(options, "figure_classname", `col-md-6 mb-4 order-${props.imageLocation == "left" ? 1 : 2}`)}>
                {cmsState.images.has(props.image) &&
                    ImageContainer(
                      cmsState.images.get(props.image),
                      props.image_size,
                      getClassName(options, "image_classname", "w-100"),
                      props.image_alt_text,
                      props.get_default_data_src,
                      props.get_default_data_srcset
                    ).run(_ => { })}
              </div>
            </div>
          </InView>
        )
      },
    )
  }
}
